/**
 * @file ui/src/pages/Admin.tsx
 * @description Spark 2.0 Operations Admin Console
 * @copyright 2026 Seth Zhai. BSL 1.1
 *
 * ADR-141（2026-04-13）：新增邀请码管理视图
 *   - 列表：显示所有码 + 使用次数 + 剩余次数 + 备注
 *   - 创建：自定义码 或 随机生成，支持次数限制和备注
 *   - 详情：查看使用此码注册的用户
 *   - 删除：带确认的删除操作
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth'
import { useAdminCopy, useAdminI18n } from '../adminI18n'
import styles from './Admin.module.css'

const CLIENT_CHANNEL_LABELS: Record<string, string> = {
  chrome_extension: 'Chrome 扩展',
  edge_extension: 'Edge 扩展',
  openai_mcp: 'OpenAI MCP',
  coze_plugin: 'Coze 插件',
  api: 'API',
  unknown: '未知',
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface AdminUser {
  id:          string
  email:       string
  plan:        'free' | 'pro' | 'enterprise'
  role:        'user' | 'admin'
  disabled_at: string | null
  created_at:  string
  invite_code: string | null
  analytics_class?: 'public' | 'invite' | 'qa' | 'internal'
}

interface Stats {
  total:               number
  active:              number
  disabled:            number
  admins:              number
  publicTestUsers:     number
  invitedTestUsers:    number
  byPlan:              Record<string, number>
  recentRegistrations: number
  publicRegistration?: {
    enabled:   boolean
    limit:     number
    used:      number
    remaining: number
  }
}

interface Pagination {
  page:       number
  limit:      number
  total:      number
  totalPages: number
}

interface InviteCode {
  code:      string
  maxUses:   number
  usedCount: number
  note:      string | null
  createdAt: string
  expiresAt: string | null
}

interface InviteCodeDetail extends InviteCode {
  users: Array<{ id: string; email: string; createdAt: string }>
}

interface DailyMetrics {
  date: string
  userRegistered: number
  kneVerify: number
  sourceSupported: number
  sourceMissing: number
  needsReview: number
  highRisk: number
  notKnowledge: number
  feedbackCopied: number
  feedbackSubmitted: number
  contributionSubmitted: number
  blockingIssues: number
  chromeStoreStatusUpdated: number
}

interface AdminSystemStatus {
  status: 'ready' | 'degraded'
  service: string
  publicBaseUrl: string
  nodeEnv: string
  imageTag: string
  uptimeSeconds: number
  timestamp: string
  checks: Array<{
    name: string
    status: 'ok' | 'error'
    latencyMs: number
    message?: string
  }>
  publicRegistration: {
    enabled: boolean
    limit: number
  }
  quota: {
    betaFree: string
    pro: string
    publicLimit: number
  }
}

interface BetaMetrics {
  window: string
  betaStartedAt: string
  publicUsers: number
  activatedPublicUsers: number
  publicActivationRate: number | null
  verifiedUsers7d: number
  verifyCount7d: number
  anonymousVerifyCount7d: number
  anonymousInstallCount7d: number
  homepageInstallClicks7d: number
  privacyConsentAccepted7d: number
  extensionOpened7d: number
  onboardingViewed7d: number
  selectionCaptured7d: number
  verifyActionClicked7d: number
  sampleVerifyStarted7d: number
  firstVerifyStarted7d: number
  firstVerifyCompleted7d: number
  firstVerifyFailed7d: number
  resultViewed7d: number
  sourceDetailsOpened7d: number
  evidenceSourceOpened7d: number
  firstVerifyCompletionRate: number | null
  anonymousConverted7d: number
  anonymousConversionRate: number | null
  repeatVerifyUsers24h: number
  repeatVerifyRate24h: number | null
  repeatVerifyUsers7d: number
  repeatVerifyRate: number | null
  averageVerifyPerVerifiedUser: number | null
  contributionCount7d: number
  feedbackPackages7d: number | null
  sourceMismatchFeedback7d: number
  feedbackCopies7d: number
  feedbackSubmissionRate: number | null
  severeBlockingIssues7d: number | null
  registrations7d: number
  waitlistCount: number
  targetAiUsers7d: number
  targetAiUserRate: number | null
  sourceHintEvents7d: number
  sourceHintCoverageRate: number | null
  sourceHintWithoutSupport7d: number
  contextDependentFragments7d: number
  currentEventVerifyCount7d: number
  currentEventMissingCount7d: number
  applicabilityRejectedCount7d: number
  verifyUnitCostUsd: number
  apiCostPerUser7d: number | null
  vultrMonthlyCostUsd: number
  codexMonthlyCostUsd: number
  domainMonthlyCostUsd: number
  monthlyFixedCostUsd: number
  projectedMonthlyVerifyCount: number
  projectedMonthlyActiveUsers: number
  fixedCostPerProjectedVerifyUsd: number | null
  fixedCostPerProjectedActiveUserUsd: number | null
  betaDecision: 'fix_blockers' | 'collect_data' | 'monitor_and_improve' | 'expand_beta'
  betaWarnings: string[]
  sourceGapRanking: Array<{
    domain: string
    council: string
    claimType: string
    trustState: string
    count: number
    priority: 'high' | 'medium' | 'low'
    nextAction: string
    missingSourceType: string
    sampleClaim: string
  }>
  blockingIssueBreakdown: Array<{
    area: string
    count: number
  }>
  blockingIssueTrend: Array<{
    day: string
    area: string
    count: number
  }>
  chromeStoreReview: {
    status: 'draft' | 'submitted' | 'in_review' | 'approved' | 'rejected' | 'paused'
    note: string
    updatedBy: string
    updatedAt: string
  }
  recentBlockingIssues: Array<{
    id: string
    createdAt: string
    userId: string
    area: string
    message: string
    issueStatus: string
    reviewNote: string
  }>
  recentFeedbackPackages: Array<{
    id: string
    createdAt: string
    userId: string
    sourcePlatform: string
    trustState: string
    claimType: string
    feedbackText: string
  }>
  verifyVersionDistribution: Array<{
    clientChannel: string
    extensionVersion: string
    buildId: string
    packagedAt: string
    manifestVersion: string
    count: number
    lastSeenAt: string
  }>
  dataReadiness: {
    verifyEvents: boolean
    contributions: boolean
    feedbackPackages: boolean
    severeBlockingIssues: boolean
    apiCost: boolean
  }
}

interface PaidValidationMetrics {
  scope: 'paid_validation'
  inviteCode: string
  targetVersion: string
  windowDays: number
  phase: 'not_started' | 'pilot' | 'controlled_test' | 'decision_review'
  participantCount: number
  verifiedParticipants: number
  verifyCount: number
  activationRate: number | null
  resultViewedParticipants: number
  resultViewedRate: number | null
  sourceDetailsOpenedCount: number
  evidenceSourceOpenedCount: number
  evidenceSourceOpenedUsers: number
  evidenceSourceOpenRate: number | null
  feedbackCount: number
  helpfulFeedbackCount: number
  unclearFeedbackCount: number
  wrongSourcesFeedbackCount: number
  notEnoughEvidenceFeedbackCount: number
  blockingIssueCount: number
  repeatUsers24h: number
  repeatRate24h: number | null
  repeatUsers7d: number
  repeatRate7d: number | null
  targetVersionVerifyCount: number
  versionDistribution: Array<{
    clientChannel: string
    extensionVersion: string
    buildId: string
    count: number
    lastSeenAt: string
  }>
}

interface ReleaseChannelStatus {
  channel: 'chrome_extension' | 'edge_extension' | 'coze_plugin'
  status: 'draft' | 'packaged' | 'submitted' | 'in_review' | 'approved' | 'rejected' | 'paused'
  version: string
  note: string
  updatedBy: string
  updatedAt: string
}

interface PublishedReleaseChannel {
  channel: ReleaseChannelStatus['channel']
  status: 'available' | 'unavailable'
  version: string | null
  checkedAt: string
  sourceUrl: string
  reason: 'platform_version_not_public' | 'request_failed' | 'invalid_response' | null
  cached: boolean
}

// ── API helpers ───────────────────────────────────────────────────────────────

function useAdminApi() {
  const { token } = useAuth()
  const base    = `/v1/admin`
  const headers = {
    'Content-Type': 'application/json',
    Authorization:  `Bearer ${token ?? ''}`,
  }
  const get = async (path: string) => {
    const res = await fetch(`${base}${path}`, { headers })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  const patch = async (path: string, body?: unknown) => {
    const res = await fetch(`${base}${path}`, {
      method: 'PATCH', headers,
      body: body ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  const post = async (path: string, body: unknown) => {
    const res = await fetch(`${base}${path}`, {
      method: 'POST', headers,
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  const del = async (path: string) => {
    const res = await fetch(`${base}${path}`, { method: 'DELETE', headers })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  return { get, patch, post, del }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PlanBadge({ plan }: { plan: string }) {
  return <span className={`${styles.badge} ${styles[`plan_${plan}`]}`}>{plan.toUpperCase()}</span>
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span className={`${styles.badge} ${role === 'admin' ? styles.roleAdmin : styles.roleUser}`}>
      {role}
    </span>
  )
}

interface RowActionsProps {
  user:     AdminUser
  onAction: (userId: string, action: string, value?: string) => Promise<void>
}

function RowActions({ user, onAction }: RowActionsProps) {
  const copy = useAdminCopy()
  const t = copy.t
  const [open,    setOpen]    = useState(false)
  const [loading, setLoading] = useState(false)
  const act = async (action: string, value?: string) => {
    setLoading(true); setOpen(false)
    try { await onAction(user.id, action, value) }
    finally { setLoading(false) }
  }
  return (
    <div className={styles.actionsCell}>
      <button className={`${styles.actionBtn} ${loading ? styles.loading : ''}`}
        onClick={() => setOpen(v => !v)} disabled={loading}>
        {loading ? '···' : '⋮'}
      </button>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownSection}>PLAN</div>
          {(['free', 'pro', 'enterprise'] as const).filter(p => p !== user.plan).map(p => (
            <button key={p} className={styles.dropdownItem} onClick={() => act('plan', p)}>→ {p}</button>
          ))}
          <div className={styles.dropdownSection}>STATUS</div>
          {user.disabled_at
            ? <button className={styles.dropdownItem} onClick={() => act('enable')}>▶ {t('启用')}</button>
            : <button className={`${styles.dropdownItem} ${styles.danger}`} onClick={() => act('disable')}>⏸ {t('禁用')}</button>
          }
          <div className={styles.dropdownSection}>ROLE</div>
          {user.role === 'user'
            ? <button className={styles.dropdownItem} onClick={() => act('role', 'admin')}>↑ {t('设为管理员')}</button>
            : <button className={`${styles.dropdownItem} ${styles.danger}`} onClick={() => act('role', 'user')}>↓ {t('降为普通用户')}</button>
          }
          {user.role !== 'admin' && (
            <>
              <div className={styles.dropdownSection}>SOURCE</div>
              {!user.invite_code && (
                <button className={styles.dropdownItem} onClick={() => act('source', 'test')}>
                  → {t('标记测试不占名额')}
                </button>
              )}
              {user.invite_code === 'BETA_TEST' && (
                <button className={styles.dropdownItem} onClick={() => act('source', 'public')}>
                  → {t('恢复公开名额')}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function UserSourceBadge({ user }: { user: AdminUser }) {
  if (user.role === 'admin') {
    return <span className={`${styles.badge} ${styles.sourceAdmin}`}>admin</span>
  }
  if (user.analytics_class === 'qa') {
    return <span className={`${styles.badge} ${styles.sourceTest}`}>qa</span>
  }
  if (user.invite_code) {
    if (user.invite_code === 'BETA_TEST') {
      return <span className={`${styles.badge} ${styles.sourceTest}`}>test</span>
    }
    return <span className={`${styles.badge} ${styles.sourceInvite}`}>invite</span>
  }
  return <span className={`${styles.badge} ${styles.sourcePublic}`}>public</span>
}

function StatsCards({ stats }: { stats: Stats }) {
  const copy = useAdminCopy()
  const t = copy.t
  const publicLimit = stats.publicRegistration?.limit ?? 200
  const publicUsed = stats.publicRegistration?.used ?? stats.publicTestUsers ?? 0
  const publicRemaining = stats.publicRegistration?.remaining ?? Math.max(0, publicLimit - publicUsed)
  const cards = [
    { label: '总账号（含测试）', value: stats.total },
    { label: '可用账号（含测试）', value: stats.active, accent: true },
    { label: '已禁用',   value: stats.disabled },
    { label: `公开 ${publicUsed}/${publicLimit}`, value: publicRemaining, accent: true },
    { label: '邀请/测试', value: stats.invitedTestUsers ?? 0 },
    { label: '管理员',   value: stats.admins },
    { label: '新增 7d',  value: stats.recentRegistrations },
    { label: 'Pro',      value: stats.byPlan['pro'] ?? 0,  accent: true },
    { label: 'Enterprise', value: stats.byPlan['enterprise'] ?? 0, accent: true },
  ]
  return (
    <div className={styles.statsGrid}>
      {cards.map(c => (
        <div key={c.label} className={`${styles.statCard} ${c.accent ? styles.statCardAccent : ''}`}>
          <span className={styles.statValue}>{c.value}</span>
          <span className={styles.statLabel}>{t(c.label)}</span>
        </div>
      ))}
    </div>
  )
}

// ── 数据看板视图 ───────────────────────────────────────────────────────────────

function AnalyticsView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()

  const [metrics, setMetrics] = useState<DailyMetrics[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [days, setDays] = useState(7)

  const loadMetrics = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const d = await api.get(`/analytics/recent?days=${days}`) as { metrics: DailyMetrics[] }
      setMetrics(d.metrics)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days])

  useEffect(() => { loadMetrics() }, [loadMetrics])

  const fmt = (s: string) =>
    new Date(s).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })

  // 计算总计
  const totals = metrics.reduce((acc, m) => ({
    userRegistered: acc.userRegistered + m.userRegistered,
    kneVerify: acc.kneVerify + m.kneVerify,
    sourceSupported: acc.sourceSupported + m.sourceSupported,
    sourceMissing: acc.sourceMissing + m.sourceMissing,
    needsReview: acc.needsReview + m.needsReview,
    highRisk: acc.highRisk + m.highRisk,
    notKnowledge: acc.notKnowledge + m.notKnowledge,
    feedbackCopied: acc.feedbackCopied + m.feedbackCopied,
    feedbackSubmitted: acc.feedbackSubmitted + m.feedbackSubmitted,
    contributionSubmitted: acc.contributionSubmitted + m.contributionSubmitted,
    blockingIssues: acc.blockingIssues + m.blockingIssues,
    chromeStoreStatusUpdated: acc.chromeStoreStatusUpdated + m.chromeStoreStatusUpdated,
  }), {
    userRegistered: 0,
    kneVerify: 0,
    sourceSupported: 0,
    sourceMissing: 0,
    needsReview: 0,
    highRisk: 0,
    notKnowledge: 0,
    feedbackCopied: 0,
    feedbackSubmitted: 0,
    contributionSubmitted: 0,
    blockingIssues: 0,
    chromeStoreStatusUpdated: 0,
  })

  return (
    <div>
      {/* 时间范围选择 */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('时间范围：')}</span>
        {[7, 14, 30].map(d => (
          <button
            key={d}
            style={{
              padding: '4px 12px',
              fontSize: 11,
              borderRadius: 4,
              border: '1px solid var(--border-dim)',
              background: days === d ? 'var(--green-dim)' : 'transparent',
              color: days === d ? 'var(--green)' : 'var(--text-dim)',
              cursor: 'pointer',
            }}
            onClick={() => setDays(d)}
          >
            {d} {t('天')}
          </button>
        ))}
      </div>

      {/* 统计卡片 */}
      <div className={styles.statsGrid} style={{ marginBottom: 16 }}>
        {[
          { label: '新增用户', value: totals.userRegistered, accent: true },
          { label: 'Verify 次数', value: totals.kneVerify, accent: true },
          { label: '来源支持', value: totals.sourceSupported },
          { label: '证据不足', value: totals.sourceMissing },
          { label: '反馈复制', value: totals.feedbackCopied },
          { label: '贡献提交', value: totals.contributionSubmitted },
        ].map(c => (
          <div key={c.label} className={`${styles.statCard} ${c.accent ? styles.statCardAccent : ''}`}>
            <span className={styles.statValue}>{c.value}</span>
            <span className={styles.statLabel}>{t(c.label)}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className={styles.errorBanner} style={{ marginBottom: 8 }}>
          ⚠ {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* 数据表格 */}
      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('每日数据明细')}</h2>
          <button className={styles.searchBtn} onClick={loadMetrics}>
            {t('刷新')}
          </button>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('日期')}</th>
                <th>{t('新增用户')}</th>
                <th>{t('Verify')}</th>
                <th>{t('来源支持')}</th>
                <th>{t('证据不足')}</th>
                <th>{t('待复核')}</th>
                <th>{t('高风险')}</th>
                <th>{t('非知识')}</th>
                <th>{t('反馈复制')}</th>
                <th>{t('反馈提交')}</th>
                <th>{t('贡献提交')}</th>
                <th>{t('阻断问题')}</th>
                <th>{t('商店状态')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className={styles.loadingRow}><span className={styles.pulse}>{t('加载中…')}</span></td></tr>
              ) : metrics.length === 0 ? (
                <tr><td colSpan={12} className={styles.emptyRow}>{t('暂无数据')}</td></tr>
              ) : metrics.map(m => (
                <tr key={m.date} className={styles.userRow}>
                  <td className={styles.dateCell}>{fmt(m.date)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center', color: m.userRegistered > 0 ? 'var(--green)' : undefined }}>
                    {m.userRegistered}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center', color: m.kneVerify > 0 ? 'var(--green)' : undefined }}>
                    {m.kneVerify}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.sourceSupported}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.sourceMissing}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.needsReview}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.highRisk}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.notKnowledge}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.feedbackCopied}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.feedbackSubmitted}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.contributionSubmitted}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center', color: m.blockingIssues > 0 ? 'var(--red)' : undefined }}>
                    {m.blockingIssues}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                    {m.chromeStoreStatusUpdated}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

// ── 邀请码视图 ─────────────────────────────────────────────────────────────────

function InviteCodesView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()

  const [codes,       setCodes]       = useState<InviteCode[]>([])
  const [loading,     setLoading]     = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [detail,      setDetail]      = useState<InviteCodeDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [showCreate,  setShowCreate]  = useState(false)

  // 创建表单状态
  const [createMode,  setCreateMode]  = useState<'custom' | 'random'>('random')
  const [customCode,  setCustomCode]  = useState('')
  const [genCount,    setGenCount]    = useState(1)
  const [maxUses,     setMaxUses]     = useState(1)
  const [note,        setNote]        = useState('')
  const [creating,    setCreating]    = useState(false)

  const loadCodes = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const d = await api.get('/invite-codes') as { codes: InviteCode[] }
      setCodes(d.codes)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { loadCodes() }, [loadCodes])

  const loadDetail = async (code: string) => {
    setDetailLoading(true)
    try {
      const d = await api.get(`/invite-codes/${code}`) as { code: InviteCode; users: InviteCodeDetail['users'] }
      setDetail({ ...d.code, users: d.users })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setDetailLoading(false)
    }
  }

  const handleDelete = async (code: string) => {
    if (!window.confirm(`确认删除邀请码 ${code}？\n已使用此码注册的用户不受影响。`)) return
    try {
      await api.del(`/invite-codes/${code}`)
      if (detail?.code === code) setDetail(null)
      await loadCodes()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const handleCreate = async () => {
    setCreating(true); setError(null)
    try {
      const body: Record<string, unknown> = { maxUses, note: note || undefined }
      if (createMode === 'custom') {
        body['code'] = customCode.toUpperCase()
      } else {
        body['count'] = genCount
      }
      await api.post('/invite-codes', body)
      setShowCreate(false)
      setCustomCode(''); setNote(''); setGenCount(1); setMaxUses(1)
      await loadCodes()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setCreating(false)
    }
  }

  const fmt = (s: string) =>
    new Date(s).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })

  const remaining = (inv: InviteCode) =>
    inv.maxUses === -1 ? '∞' : String(inv.maxUses - inv.usedCount)

  const isExhausted = (inv: InviteCode) =>
    inv.maxUses !== -1 && inv.usedCount >= inv.maxUses

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* 左：列表 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* 统计条 */}
        <div className={styles.statsGrid} style={{ marginBottom: 16 }}>
          {[
            { label: '邀请码总数', value: codes.length },
            { label: '可用',      value: codes.filter(c => !isExhausted(c)).length, accent: true },
            { label: '已用完',    value: codes.filter(c => isExhausted(c)).length },
            { label: '已注册用户',value: codes.reduce((s, c) => s + c.usedCount, 0), accent: true },
          ].map(c => (
            <div key={c.label} className={`${styles.statCard} ${c.accent ? styles.statCardAccent : ''}`}>
              <span className={styles.statValue}>{c.value}</span>
              <span className={styles.statLabel}>{t(c.label)}</span>
            </div>
          ))}
        </div>

        <section className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h2 className={styles.tableTitle}>{t('邀请码列表')}</h2>
            <button className={styles.searchBtn} onClick={() => setShowCreate(v => !v)}>
              {showCreate ? t('取消') : t('+ 新建')}
            </button>
          </div>

          {/* 创建表单 */}
          {showCreate && (
            <div style={{
              background: 'var(--bg-2)', border: '1px solid var(--border-dim)',
              borderRadius: 8, padding: 16, marginBottom: 12,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              {/* 模式切换 */}
              <div style={{ display: 'flex', gap: 8 }}>
                {(['random', 'custom'] as const).map(m => (
                  <button key={m}
                    style={{
                      padding: '4px 12px', fontSize: 11, borderRadius: 4, border: '1px solid var(--border-dim)',
                      background: createMode === m ? 'var(--green-dim)' : 'transparent',
                      color: createMode === m ? 'var(--green)' : 'var(--text-dim)',
                      cursor: 'pointer',
                    }}
                    onClick={() => setCreateMode(m)}
                  >
                    {t(m === 'random' ? '随机生成' : '自定义')}
                  </button>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                {createMode === 'custom' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t('邀请码')}</label>
                    <input
                      className={styles.searchInput}
                      placeholder={t('如 SPARK2026')}
                      value={customCode}
                      onChange={e => setCustomCode(e.target.value.toUpperCase())}
                      maxLength={32}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t('生成数量')}</label>
                    <input
                      className={styles.searchInput}
                      type="number" min={1} max={50}
                      value={genCount}
                      onChange={e => setGenCount(Number(e.target.value))}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t('次数上限（-1=无限）')}</label>
                  <input
                    className={styles.searchInput}
                    type="number" min={-1}
                    value={maxUses}
                    onChange={e => setMaxUses(Number(e.target.value))}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t('备注（可选）')}</label>
                  <input
                    className={styles.searchInput}
                    placeholder={t('如 研究圈')}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    maxLength={100}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className={styles.clearBtn} onClick={() => setShowCreate(false)}>{t('取消')}</button>
                <button
                  className={styles.searchBtn}
                  onClick={handleCreate}
                  disabled={creating || (createMode === 'custom' && !customCode.trim())}
                >
                  {creating ? t('创建中…') : t('确认创建')}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className={styles.errorBanner} style={{ marginBottom: 8 }}>
              ⚠ {error}
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('邀请码')}</th>
                  <th>{t('上限')}</th>
                  <th>{t('已用')}</th>
                  <th>{t('剩余')}</th>
                  <th>{t('备注')}</th>
                  <th>{t('创建时间')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className={styles.loadingRow}><span className={styles.pulse}>{t('加载中…')}</span></td></tr>
                ) : codes.length === 0 ? (
                  <tr><td colSpan={7} className={styles.emptyRow}>{t('暂无邀请码，点击「新建」创建')}</td></tr>
                ) : codes.map(inv => (
                  <tr
                    key={inv.code}
                    className={`${styles.userRow} ${isExhausted(inv) ? styles.disabledRow : ''} ${detail?.code === inv.code ? styles.selectedRow : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => loadDetail(inv.code)}
                  >
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: 1 }}>
                      {inv.code}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                      {inv.maxUses === -1 ? '∞' : inv.maxUses}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center', color: inv.usedCount > 0 ? 'var(--green)' : undefined }}>
                      {inv.usedCount}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'center', color: isExhausted(inv) ? 'var(--red)' : undefined }}>
                      {remaining(inv)}
                    </td>
                    <td className={styles.dateCell}>{inv.note ?? '—'}</td>
                    <td className={styles.dateCell}>{fmt(inv.createdAt)}</td>
                    <td>
                      <div className={styles.actionsCell} onClick={e => e.stopPropagation()}>
                        <button
                          className={`${styles.dropdownItem} ${styles.danger}`}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4 }}
                          onClick={() => handleDelete(inv.code)}
                        >
                          {t('删除')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* 右：详情面板 */}
      {(detail || detailLoading) && (
        <div style={{
          width: 300, flexShrink: 0,
          background: 'var(--bg-2)', border: '1px solid var(--border-dim)',
          borderRadius: 10, padding: 16,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('邀请码详情')}</span>
            <button className={styles.clearBtn} style={{ fontSize: 11 }} onClick={() => setDetail(null)}>✕</button>
          </div>

          {detailLoading ? (
            <span className={styles.pulse} style={{ fontSize: 12 }}>{t('加载中…')}</span>
          ) : detail && (
            <>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, letterSpacing: 2, color: 'var(--green)' }}>
                {detail.code}
              </div>

              {[
                ['次数上限', detail.maxUses === -1 ? t('无限制') : String(detail.maxUses)],
                ['已使用',   String(detail.usedCount)],
                ['剩余',     remaining(detail)],
                ['备注',     detail.note ?? '—'],
                ['创建时间', fmt(detail.createdAt)],
                ['过期时间', detail.expiresAt ? fmt(detail.expiresAt) : t('永不过期')],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-dim)' }}>{t(k)}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{v}</span>
                </div>
              ))}

              <div style={{ borderTop: '1px solid var(--border-dim)', paddingTop: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8 }}>
                  {t('使用此码注册的用户')}（{detail.users.length}）
                </div>
                {detail.users.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>{t('暂无')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {detail.users.map(u => (
                      <div key={u.id} style={{ fontSize: 11 }}>
                        <div style={{ color: 'var(--text)' }}>{u.email}</div>
                        <div style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                          {fmt(u.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function SystemStatusView({ stats }: { stats: Stats | null }) {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()
  const publicRegistration = stats?.publicRegistration
  const [systemStatus, setSystemStatus] = useState<AdminSystemStatus | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)

  const loadSystemStatus = useCallback(async () => {
    setStatusLoading(true)
    setStatusError(null)
    try {
      const data = await api.get('/system-status') as { systemStatus: AdminSystemStatus }
      setSystemStatus(data.systemStatus)
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : String(error))
    } finally {
      setStatusLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadSystemStatus()
  }, [loadSystemStatus])

  const fmtSystemTime = (s: string) =>
    new Date(s).toLocaleString(copy.locale === 'en' ? 'en-US' : 'zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  const compactImageTag = (tag?: string) => {
    if (!tag) return '—'
    const value = tag.includes(':') ? tag.split(':').pop() ?? tag : tag
    return value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-5)}` : value
  }

  const dependencyRows = systemStatus?.checks.map(check => [
    check.name,
    `${check.status}${typeof check.latencyMs === 'number' ? ` · ${check.latencyMs}ms` : ''}`,
  ]) ?? [['状态', statusLoading ? 'loading' : 'not loaded']]

  const systemCards = [
    {
      title: 'Spark Gateway',
      state: systemStatus?.status === 'ready' ? 'ready' : systemStatus?.status === 'degraded' ? 'degraded' : 'loading',
      ok: systemStatus?.status === 'ready',
      rows: [
        ['服务', systemStatus?.service ?? 'spark-api'],
        ['公网 API', systemStatus?.publicBaseUrl ?? 'https://knespark.com'],
        ['镜像标记', systemStatus?.imageTag ?? '—'],
        ['运行时长', systemStatus ? `${systemStatus.uptimeSeconds}s` : '—'],
        ['更新时间', systemStatus?.timestamp ? fmtSystemTime(systemStatus.timestamp) : '—'],
      ],
    },
    {
      title: 'Dependencies',
      state: systemStatus?.status === 'ready' ? '全部正常' : '需要检查',
      ok: systemStatus?.status === 'ready',
      rows: dependencyRows,
    },
    {
      title: 'Usage & Quota',
      state: '已启用',
      ok: true,
      rows: [
        ['Beta Free', systemStatus?.quota.betaFree ?? '50 / 5h'],
        ['Pro', systemStatus?.quota.pro ?? '120 / 5h'],
        ['Public Limit', `${systemStatus?.quota.publicLimit ?? 200} ordinary users`],
      ],
    },
    {
      title: 'Public Registration',
      state: (systemStatus?.publicRegistration.enabled ?? publicRegistration?.enabled) ? '可公开注册' : '默认关闭',
      ok: Boolean(systemStatus?.publicRegistration.enabled ?? publicRegistration?.enabled),
      rows: [
        ['公开名额', `${publicRegistration?.used ?? stats?.publicTestUsers ?? 0}/${publicRegistration?.limit ?? 200}`],
        ['剩余名额', `${publicRegistration?.remaining ?? '—'}`],
        ['不占名额', 'admin / invite / test'],
      ],
    },
  ]

  const boundaries = [
    ['已停用方向', '旧 Spark Chat 不作为主入口，只保留 fallback / retired route 说明。'],
    ['环境边界', '本地使用 .env，线上使用 K8s Secret；Secret 不写入文档或 Git。'],
    ['后台边界', 'KNE 审核、来源库、可信策略迁移到 /kneadmin。'],
    ['运营边界', '本后台只处理用户、注册、用量、日志、成本和系统状态。'],
  ]

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Spark 2.0 System</p>
          <h1 className={styles.heroTitle}>{t('系统状态')}</h1>
          <p className={styles.heroText}>
            {t('面向浏览器外挂、公开测试、用户注册、用量限额和线上服务的运营状态。KNE 治理状态迁移到 /kneadmin。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{systemStatus?.status ?? '—'}</span>
            <small>{t('实时状态')}</small>
          </div>
          <div>
            <span>{systemStatus?.checks.filter(check => check.status === 'ok').length ?? '—'}</span>
            <small>{t('依赖正常')}</small>
          </div>
          <div>
            <span>{compactImageTag(systemStatus?.imageTag)}</span>
            <small>{t('当前镜像')}</small>
          </div>
        </div>
      </section>

      {statusError && (
        <div className={styles.errorBanner}>
          ⚠ {statusError}
          <button onClick={() => setStatusError(null)}>✕</button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className={styles.searchBtn} disabled={statusLoading} onClick={loadSystemStatus}>
          {statusLoading ? t('刷新中…') : t('刷新状态')}
        </button>
      </div>

      <div className={styles.moduleGrid}>
        {systemCards.map(card => (
          <article key={card.title} className={styles.moduleCard}>
            <span className={styles.moduleLabel}>
              <span className={`${styles.statusLight} ${card.ok ? styles.statusLightOn : styles.statusLightWarn}`} />
              {t(card.state)}
            </span>
            <strong>{card.title}</strong>
            <div className={styles.systemRows}>
              {card.rows.map(([label, value]) => (
                <div key={label}>
                  <span>{t(label)}</span>
                  <p>{t(value)}</p>
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('系统边界检查')}</h2>
        </div>
        <div className={styles.checklist}>
          {boundaries.map(([title, body]) => (
            <div key={title} className={styles.checkItem}>
              <span />
              <div>
                <strong>{t(title)}</strong>
                <p>{t(body)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function SparkOpsOverviewView({ stats }: { stats: Stats | null }) {
  const copy = useAdminCopy()
  const t = copy.t
  const publicRegistration = stats?.publicRegistration
  const publicRemainingText = publicRegistration?.remaining ?? '—'
  const cards = [
    {
      label: '公开测试',
      value: `${publicRegistration?.used ?? stats?.publicTestUsers ?? 0}/${publicRegistration?.limit ?? 200}`,
      detail: copy.locale === 'en'
        ? `${publicRemainingText} ordinary public registration slots remaining.`
        : `剩余 ${publicRemainingText} 个普通公开注册名额。`,
    },
    {
      label: '历史账号',
      value: String(stats?.invitedTestUsers ?? 0),
      detail: 'Spark 1.x 历史账号和邀请码测试用户不占 200 名额。',
    },
    {
      label: '注册状态',
      value: publicRegistration?.enabled ? 'Open' : 'Closed',
      detail: 'Chrome Web Store 审核期保持公开注册开启，达到上限自动停止。',
    },
    {
      label: '运营重点',
      value: 'Monitor',
      detail: '关注注册、Verify 用量、限流、错误日志、成本与用户反馈。',
    },
  ]

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Spark Operations</p>
          <h1 className={styles.heroTitle}>{t('Spark 运营后台')}</h1>
          <p className={styles.heroText}>
            {t('管理公开测试、用户、使用量、日志、成本和 Chrome Web Store 状态。KNE 贡献审核、来源库和可信策略迁移到 /kneadmin。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{stats?.total ?? '—'}</span>
            <small>{t('总账号')}</small>
          </div>
          <div>
            <span>{stats?.recentRegistrations ?? '—'}</span>
            <small>{t('近 7 天新增')}</small>
          </div>
          <div>
            <span>{publicRegistration?.enabled ? 'Open' : 'Closed'}</span>
            <small>{t('公开注册')}</small>
          </div>
        </div>
      </section>

      <div className={styles.moduleGrid}>
        {cards.map(card => (
          <article key={card.label} className={styles.moduleCard}>
            <span className={styles.moduleLabel}>{t(card.label)}</span>
            <strong>{t(card.value)}</strong>
            <p>{t(card.detail)}</p>
          </article>
        ))}
      </div>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('运营边界')}</h2>
        </div>
        <div className={styles.checklist}>
          {[
            ['保留在 /admin', '用户、注册、邀请码、成本、系统、三端发布状态，以及自然流量与付费验证队列。'],
            ['保留在 /kneadmin', '检索质量、Evidence Fit、Trust State、来源缺口、来源库、证据片段与知识治理。'],
            ['样本隔离', '无邀请码自然用户、匿名试用、付费邀请码和工程账号必须分别统计。'],
            ['发布边界', 'Chrome、Edge、Coze 分别记录打包、提交、审核和批准；审核中不等于已上线。'],
          ].map(([title, body]) => (
            <div key={title} className={styles.checkItem}>
              <span />
              <div>
                <strong>{t(title)}</strong>
                <p>{t(body)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function PublicBetaMonitorView({ stats }: { stats: Stats | null }) {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()
  const [metrics, setMetrics] = useState<BetaMetrics | null>(null)
  const [metricsError, setMetricsError] = useState<string | null>(null)
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [metricsUpdatedAt, setMetricsUpdatedAt] = useState<string | null>(null)
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([])
  const [selectedInviteCode, setSelectedInviteCode] = useState('')
  const [validationMetrics, setValidationMetrics] = useState<PaidValidationMetrics | null>(null)
  const [validationLoading, setValidationLoading] = useState(false)
  const [releaseChannels, setReleaseChannels] = useState<ReleaseChannelStatus[]>([])
  const [savingChannel, setSavingChannel] = useState<string | null>(null)
  const [publishedChannels, setPublishedChannels] = useState<PublishedReleaseChannel[]>([])
  const [publishedLoading, setPublishedLoading] = useState(false)
  const [publishedCheckedAt, setPublishedCheckedAt] = useState<string | null>(null)

  const loadMetrics = async () => {
    setMetricsLoading(true)
    setMetricsError(null)
    try {
      const data = await api.get('/beta-metrics') as { metrics: BetaMetrics }
      setMetrics(data.metrics)
    } catch (error) {
      setMetricsError(error instanceof Error ? error.message : String(error))
    } finally {
      setMetricsLoading(false)
      setMetricsUpdatedAt(new Date().toISOString())
    }
  }

  const resolveBlockingIssue = async (id: string) => {
    setMetricsError(null)
    try {
      await api.patch(`/beta-issues/${id}/status`, { status: 'resolved', reviewNote: 'Marked resolved in Admin.' })
      await loadMetrics()
    } catch (error) {
      setMetricsError(error instanceof Error ? error.message : String(error))
    }
  }

  const loadValidationMetrics = async (inviteCode: string) => {
    if (!inviteCode) {
      setValidationMetrics(null)
      return
    }
    setValidationLoading(true)
    setMetricsError(null)
    try {
      const data = await api.get(`/validation-metrics?inviteCode=${encodeURIComponent(inviteCode)}&days=14`) as { metrics: PaidValidationMetrics }
      setValidationMetrics(data.metrics)
    } catch (error) {
      setMetricsError(error instanceof Error ? error.message : String(error))
    } finally {
      setValidationLoading(false)
    }
  }

  const updateReleaseChannel = (
    channel: ReleaseChannelStatus['channel'],
    changes: Partial<Pick<ReleaseChannelStatus, 'status' | 'version' | 'note'>>,
  ) => {
    setReleaseChannels(current => current.map(item => item.channel === channel ? { ...item, ...changes } : item))
  }

  const saveReleaseChannel = async (channel: ReleaseChannelStatus) => {
    setSavingChannel(channel.channel)
    setMetricsError(null)
    try {
      const data = await api.patch(`/release-channels/${channel.channel}`, {
        status: channel.status,
        version: channel.version,
        note: channel.note.trim() || undefined,
      }) as { channel: ReleaseChannelStatus }
      setReleaseChannels(current => current.map(item => item.channel === channel.channel ? data.channel : item))
    } catch (error) {
      setMetricsError(error instanceof Error ? error.message : String(error))
    } finally {
      setSavingChannel(null)
    }
  }

  const loadPublishedChannels = async (forceRefresh = false) => {
    setPublishedLoading(true)
    setMetricsError(null)
    try {
      const data = await api.get(`/release-channels/published${forceRefresh ? '?refresh=1' : ''}`) as {
        checkedAt: string
        channels: PublishedReleaseChannel[]
      }
      setPublishedChannels(data.channels)
      setPublishedCheckedAt(data.checkedAt)
    } catch (error) {
      setMetricsError(error instanceof Error ? error.message : String(error))
    } finally {
      setPublishedLoading(false)
    }
  }

  useEffect(() => {
    void loadMetrics()
    void api.get('/invite-codes')
      .then((data: { codes: InviteCode[] }) => setInviteCodes(data.codes))
      .catch((error: Error) => setMetricsError(error.message))
    void api.get('/release-channels')
      .then((data: { channels: ReleaseChannelStatus[] }) => setReleaseChannels(data.channels))
      .catch((error: Error) => setMetricsError(error.message))
    void loadPublishedChannels()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    void loadValidationMetrics(selectedInviteCode)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInviteCode])

  const publicRegistration = stats?.publicRegistration
  const publicLimit = publicRegistration?.limit ?? 200
  const publicUsed = publicRegistration?.used ?? stats?.publicTestUsers ?? 0
  const publicRemaining = publicRegistration?.remaining ?? Math.max(0, publicLimit - publicUsed)
  const capReached = publicRemaining <= 0
  const activeRate = metrics?.publicActivationRate ?? null
  const fmt = (s: string) => new Date(s).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const countText = (value: number, zhUnit: string, enUnit: string) =>
    copy.locale === 'en' ? `${value} ${enUnit}` : `${value}${zhUnit}`
  const pendingText = () => t('加载中')
  const noDataText = () => t('无数据')
  const notCollectedText = () => t('未采集')
  const notConfiguredText = () => t('未配置')

  const current = {
    publicUsers: `${publicUsed}/${publicLimit}`,
    activeRate: activeRate === null ? noDataText() : `${activeRate}%`,
    repeatVerify: metrics?.repeatVerifyRate === null || metrics?.repeatVerifyRate === undefined ? noDataText() : `${metrics.repeatVerifyRate}%`,
    averageVerify: metrics?.averageVerifyPerVerifiedUser === null || metrics?.averageVerifyPerVerifiedUser === undefined ? noDataText() : countText(metrics.averageVerifyPerVerifiedUser, '次', 'times'),
    anonymousVerify: metrics ? `${metrics.anonymousVerifyCount7d}/${metrics.anonymousInstallCount7d}` : pendingText(),
    homepageInstallClicks: metrics ? countText(metrics.homepageInstallClicks7d, '次', 'clicks') : pendingText(),
    firstVerifyFunnel: metrics
      ? `${metrics.privacyConsentAccepted7d} → ${metrics.extensionOpened7d} → ${metrics.selectionCaptured7d} → ${metrics.verifyActionClicked7d} → ${metrics.firstVerifyStarted7d} → ${metrics.firstVerifyCompleted7d} → ${metrics.resultViewed7d}`
      : pendingText(),
    firstVerifyCompletion: metrics?.firstVerifyCompletionRate === null || metrics?.firstVerifyCompletionRate === undefined ? noDataText() : `${metrics.firstVerifyCompletionRate}%`,
    repeatVerify24h: metrics?.repeatVerifyRate24h === null || metrics?.repeatVerifyRate24h === undefined ? noDataText() : `${metrics.repeatVerifyUsers24h}/${metrics.firstVerifyCompleted7d} · ${metrics.repeatVerifyRate24h}%`,
    anonymousConversion: metrics?.anonymousConversionRate === null || metrics?.anonymousConversionRate === undefined ? noDataText() : `${metrics.anonymousConverted7d}/${metrics.firstVerifyCompleted7d} · ${metrics.anonymousConversionRate}%`,
    feedbackPackages: metrics?.feedbackPackages7d === null || metrics?.feedbackPackages7d === undefined ? notCollectedText() : countText(metrics.feedbackPackages7d, '份', 'packages'),
    feedbackFunnel: metrics?.feedbackSubmissionRate === null || metrics?.feedbackSubmissionRate === undefined ? noDataText() : `${metrics.feedbackPackages7d}/${metrics.feedbackCopies7d} · ${metrics.feedbackSubmissionRate}%`,
    contributionSubmits: metrics ? countText(metrics.contributionCount7d, '条', 'items') : pendingText(),
    severeBugs: metrics?.severeBlockingIssues7d === null || metrics?.severeBlockingIssues7d === undefined ? notCollectedText() : countText(metrics.severeBlockingIssues7d, '个', 'items'),
    targetAiUsers: metrics?.targetAiUserRate === null || metrics?.targetAiUserRate === undefined ? noDataText() : `${metrics.targetAiUsers7d}/${metrics.verifiedUsers7d} · ${metrics.targetAiUserRate}%`,
    sourceHintCoverage: metrics?.sourceHintCoverageRate === null || metrics?.sourceHintCoverageRate === undefined ? noDataText() : `${metrics.sourceHintCoverageRate}%`,
    sourceHintsWithoutSupport: metrics ? countText(metrics.sourceHintWithoutSupport7d, '次', 'times') : pendingText(),
    contextDependentFragments: metrics ? countText(metrics.contextDependentFragments7d, '次', 'times') : pendingText(),
    sourceDetailsOpened: metrics ? countText(metrics.sourceDetailsOpened7d, '次', 'times') : pendingText(),
    evidenceSourceOpened: metrics ? countText(metrics.evidenceSourceOpened7d, '次', 'times') : pendingText(),
    sourceMismatchFeedback: metrics ? countText(metrics.sourceMismatchFeedback7d, '次', 'times') : pendingText(),
    currentEventHealth: metrics
      ? `${metrics.currentEventMissingCount7d}/${metrics.currentEventVerifyCount7d}`
      : pendingText(),
    applicabilityRejected: metrics ? countText(metrics.applicabilityRejectedCount7d, '次', 'times') : pendingText(),
    variableApiCost: metrics?.apiCostPerUser7d === null || metrics?.apiCostPerUser7d === undefined ? notConfiguredText() : `$${metrics.apiCostPerUser7d}`,
    verifyUnitCost: metrics?.verifyUnitCostUsd ? `$${metrics.verifyUnitCostUsd}` : notConfiguredText(),
    monthlyFixedCost: metrics?.monthlyFixedCostUsd ? `$${metrics.monthlyFixedCostUsd}` : notConfiguredText(),
    vultrMonthlyCost: metrics?.vultrMonthlyCostUsd ? `$${metrics.vultrMonthlyCostUsd}` : notConfiguredText(),
    codexMonthlyCost: metrics?.codexMonthlyCostUsd ? `$${metrics.codexMonthlyCostUsd}` : notConfiguredText(),
    domainMonthlyCost: metrics?.domainMonthlyCostUsd ? `$${metrics.domainMonthlyCostUsd}` : notConfiguredText(),
    projectedMonthlyVerify: metrics ? countText(metrics.projectedMonthlyVerifyCount, '次', 'times') : pendingText(),
    projectedMonthlyActiveUsers: metrics ? countText(metrics.projectedMonthlyActiveUsers, '人', 'users') : pendingText(),
    fixedCostPerVerify: metrics?.fixedCostPerProjectedVerifyUsd === null || metrics?.fixedCostPerProjectedVerifyUsd === undefined ? noDataText() : `$${metrics.fixedCostPerProjectedVerifyUsd}`,
    fixedCostPerActiveUser: metrics?.fixedCostPerProjectedActiveUserUsd === null || metrics?.fixedCostPerProjectedActiveUserUsd === undefined ? noDataText() : `$${metrics.fixedCostPerProjectedActiveUserUsd}`,
  }
  const readiness = metrics?.dataReadiness
    ? [
      ['Verify', metrics.dataReadiness.verifyEvents],
      ['Contribution', metrics.dataReadiness.contributions],
      ['Feedback', metrics.dataReadiness.feedbackPackages],
      ['Blocking', metrics.dataReadiness.severeBlockingIssues],
      ['Cost', metrics.dataReadiness.apiCost],
    ].map(([label, ready]) => `${label}:${ready ? 'ok' : 'todo'}`).join(' · ')
    : pendingText()
  const decisionLabel: Record<NonNullable<BetaMetrics['betaDecision']>, string> = {
    fix_blockers: '先修阻断',
    collect_data: '继续收集',
    monitor_and_improve: '监控优化',
    expand_beta: '可扩大测试',
  }
  const decisionDetail: Record<NonNullable<BetaMetrics['betaDecision']>, string> = {
    fix_blockers: '存在严重阻断问题，先修复再扩大测试。',
    collect_data: '真实使用样本仍少，先开放并观察目标用户行为。',
    monitor_and_improve: '已有数据但未达到扩大测试门槛，优先处理留存、反馈和 KNE Admin 中的来源缺口。',
    expand_beta: '核心留存、频次和反馈达到扩大测试门槛。',
  }

  const goals = [
    ['公开测试注册', current.publicUsers, '上限 200 人；历史账号、管理员、邀请测试不占普通名额。'],
    ['目标 AI 用户', current.targetAiUsers, '后台自动识别：7 天内在 ChatGPT、千问、Claude、豆包或 Gemini 页面触发 Verify 的用户。'],
    ['激活率', current.activeRate, '目标 >= 40%；定义为注册后完成至少 1 次 Verify。'],
    ['7 天二次验证率', current.repeatVerify, '目标 >= 20%；融资验证优先看回访，不只看安装。'],
    ['人均 7 天 Verify', current.averageVerify, '目标 >= 5 次；判断插件是否进入日常 AI 使用。'],
    ['匿名试用 Verify', current.anonymousVerify, '格式为 Verify 次数 / 匿名安装数；不计入 200 名额、注册激活或留存。'],
    ['官网安装点击', current.homepageInstallClicks, '官网安装入口的聚合点击量；只记录语言、入口位置和活动标签，不记录选中文本或账号标识。'],
    ['首次验证漏斗', current.firstVerifyFunnel, '隐私同意 → 插件打开 → 捕获选择 → 点击验证 → 发出请求 → 完成验证 → 看到结果；仅统计已同意数据使用说明的真实流量。'],
    ['查看来源详情', current.sourceDetailsOpened, '用户在看到结论后主动展开支持来源、背景来源、冲突或证据缺口的次数。'],
    ['打开外部证据', current.evidenceSourceOpened, '用户实际点击来源链接的次数；与仅展开来源详情分开统计。'],
    ['首次验证完成率', current.firstVerifyCompletion, '目标 >= 40%；完成首次验证人数 / 插件打开人数。'],
    ['24 小时二次验证率', current.repeatVerify24h, '目标 >= 15%；观察首次价值是否促成短期再次使用。'],
    ['匿名转注册', current.anonymousConversion, '匿名完成首次验证后登录或注册的转化，不作为首次使用门槛。'],
    ['有效反馈包', current.feedbackPackages, '目标 >= 30 份；包含可靠性判断、来源强弱、理解难点、复用意愿。'],
    ['反馈提交转化', current.feedbackFunnel, '复制反馈包后真正提交的比例；用于判断反馈入口是否太重。'],
    ['来源不匹配反馈', current.sourceMismatchFeedback, '用户明确反馈来源不适用或不相关；优先检查 Applicability Gate。'],
    ['知识贡献提交', current.contributionSubmits, '目标 >= 20 条；有实质知识内容的反馈再转入 KNE Review。'],
    ['来源线索覆盖率', current.sourceHintCoverage, 'Verify 后至少给出来源线索的比例；用于观察“未知”是否仍有可行动方向。'],
    ['有线索但未支持', current.sourceHintsWithoutSupport, '有候选来源方向但未进入 KNE 支持层；优先进入 source gap 补库。'],
    ['上下文不足选择', current.contextDependentFragments, '用户选择的片段缺少必要上下文；属于插件交互优化信号，不进入 KNE 来源缺口。'],
    ['当前事件证据不足', current.currentEventHealth, '格式为证据不足或待复核 / 当前事件 Verify；观察时效性来源覆盖。'],
    ['来源适用性拦截', current.applicabilityRejected, '候选来源因主体、语境或来源类型不适用而被 Gate 排除的 Verify 次数。'],
    ['严重阻断问题', current.severeBugs, '目标 7 天内 0 个；优先修登录、Verify、限流、来源识别阻断。'],
    ['固定月成本', current.monthlyFixedCost, 'Vultr、其他成本和 Codex Pro 5x 的月度固定投入合计。'],
    ['Vultr 月成本', current.vultrMonthlyCost, '服务器与云资源固定成本。'],
    ['Codex Pro 5x 月成本', current.codexMonthlyCost, '开发与运维使用的固定工具成本。'],
    ['其他月成本', current.domainMonthlyCost, '域名、VPN、企业邮箱等其它固定月成本。'],
    ['预计月 Verify', current.projectedMonthlyVerify, '按最近 7 天 Verify 次数线性外推，仅用于公测成本观察。'],
    ['预计月活跃验证用户', current.projectedMonthlyActiveUsers, '按最近 7 天已验证用户线性外推。'],
    ['固定成本 / Verify', current.fixedCostPerVerify, '固定月成本按预计月 Verify 摊销；公测早期会偏高。'],
    ['固定成本 / 活跃用户', current.fixedCostPerActiveUser, '固定月成本按预计月活跃验证用户摊销。'],
    ['单次 Verify API 变量成本', current.verifyUnitCost, '来自 SPARK_BETA_VERIFY_UNIT_COST_USD；不包含服务器、其他固定成本和 Codex。'],
    ['单用户 API 变量成本', current.variableApiCost, '只统计按 Verify 调用产生的变量成本。'],
  ]
  const clientChannelSummary = (['chrome_extension', 'edge_extension', 'coze_plugin'] as const).map(clientChannel => {
    const rows = (metrics?.verifyVersionDistribution ?? []).filter(item => item.clientChannel === clientChannel)
    return {
      clientChannel,
      count: rows.reduce((sum, item) => sum + item.count, 0),
    }
  })

  const gates = [
    ['0.3.1 发布就绪', 'R7B 生产 QA 通过；Chrome、Edge、Coze 包与线上接口均为 0.3.1；无阻断问题。'],
    ['3 人付费试点', '只使用独立邀请码；确认安装、首次 Verify、结果展示、外部证据点击和反馈链路可用。'],
    ['12–15 人受控验证', '完成试点后扩大；观察 24 小时复用、7 天复用、真实证据点击、来源错误和阻断问题。'],
    ['阶段决策复盘', '样本只作方向性判断；根据真实失败分布决定优化 R2–R4、触发 R5，或停止产品验证。'],
  ]

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Release & Validation Monitor</p>
          <h1 className={styles.heroTitle}>{t('发布与付费验证监控')}</h1>
          <p className={styles.heroText}>
            {t('0.3.1 发布状态、付费测试队列和自然流量分开统计。付费参与不冒充自然需求，工程测试不冒充用户验证。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{validationMetrics?.participantCount ?? '—'}</span>
            <small>{t('付费参与者')}</small>
          </div>
          <div>
            <span>{validationMetrics?.evidenceSourceOpenedUsers ?? '—'}</span>
            <small>{t('打开证据用户')}</small>
          </div>
          <div>
            <span>{validationMetrics?.repeatRate24h === null || validationMetrics?.repeatRate24h === undefined ? '—' : `${validationMetrics.repeatRate24h}%`}</span>
            <small>{t('24h 复用')}</small>
          </div>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <div>
            <h2 className={styles.tableTitle}>{t('付费验证队列')}</h2>
            <p className={styles.sectionHint}>{t('必须选择专用邀请码；这里不包含管理员、自然注册、匿名试用或历史工程账号。')}</p>
          </div>
          <select
            className={styles.searchInput}
            value={selectedInviteCode}
            onChange={event => setSelectedInviteCode(event.target.value)}
          >
            <option value="">{t('选择专用邀请码')}</option>
            {inviteCodes.map(invite => (
              <option key={invite.code} value={invite.code}>
                {invite.code} · {invite.usedCount}/{invite.maxUses}{invite.note ? ` · ${invite.note}` : ''}
              </option>
            ))}
          </select>
        </div>
        {selectedInviteCode ? (
          <>
            <div className={styles.moduleGrid}>
              {[
                ['阶段', validationMetrics?.phase ?? (validationLoading ? 'loading' : '—'), 'pilot=最多3人；controlled_test=4–11人；decision_review=12人以上。'],
                ['参与 / 已验证', validationMetrics ? `${validationMetrics.participantCount} / ${validationMetrics.verifiedParticipants}` : '—', '邀请码注册人数 / 完成至少一次生产 Verify 的人数。'],
                ['实际打开证据', validationMetrics ? `${validationMetrics.evidenceSourceOpenedUsers}人 / ${validationMetrics.evidenceSourceOpenedCount}次` : '—', '点击外部来源链接，不等同于展开来源面板。'],
                ['24h / 7d 复用', validationMetrics ? `${validationMetrics.repeatRate24h ?? '—'}% / ${validationMetrics.repeatRate7d ?? '—'}%` : '—', '同一参与者首次验证后再次主动使用。'],
                ['有效反馈', validationMetrics?.feedbackCount ?? '—', validationMetrics ? `有帮助 ${validationMetrics.helpfulFeedbackCount} · 来源错误 ${validationMetrics.wrongSourcesFeedbackCount} · 证据太少 ${validationMetrics.notEnoughEvidenceFeedbackCount}` : '—'],
                ['目标版本覆盖', validationMetrics ? `${validationMetrics.targetVersionVerifyCount}/${validationMetrics.verifyCount}` : '—', `仅 ${validationMetrics?.targetVersion ?? '0.3.1'} 可进入正式付费验证结论。`],
                ['阻断问题', validationMetrics?.blockingIssueCount ?? '—', '目标为 0；出现后暂停扩大样本并先修复。'],
              ].map(([label, value, detail]) => (
                <article key={label} className={styles.moduleCard}>
                  <span className={styles.moduleLabel}>{t(String(label))}</span>
                  <strong>{String(value)}</strong>
                  <p>{t(String(detail))}</p>
                </article>
              ))}
            </div>
          </>
        ) : (
          <p className={styles.sectionHint}>{t('尚未选择队列。不要默认复用 BETA_TEST 等历史邀请码，以免旧测试账号污染付费验证样本。')}</p>
        )}
      </section>

      <div className={styles.moduleGrid}>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>Beta Cap</span>
          <strong>{publicUsed}/{publicLimit}</strong>
          <p>{capReached ? t('公开测试名额已满，普通注册应显示等待名单提示。') : t('普通公开测试用户上限。达到上限后停止普通注册，管理员和邀请测试保留。')}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>Data Boundary</span>
          <strong>{metrics?.betaStartedAt ? fmt(metrics.betaStartedAt) : '—'}</strong>
          <p>{t('公测核心指标只统计该时间之后的普通公开测试用户和行为；更早数据保留为测试/工程记录。')}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>Waitlist</span>
          <strong>{metrics?.waitlistCount ?? '—'}</strong>
          <p>{t('名额触顶后统计等待名单人数，用于判断下一轮开放节奏。')}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>Recent Growth</span>
          <strong>{stats?.recentRegistrations ?? '—'}</strong>
          <p>{t('近 7 天新增账号。用于观察 Chrome Web Store 上线后的自然进入速度。')}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>Activated Users</span>
          <strong>{metrics?.activatedPublicUsers ?? '—'}</strong>
          <p>{t('公开测试用户中至少完成过 1 次 Verify 的人数。')}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>Data Hooks</span>
          <strong>{metricsError ? 'Metrics Error' : 'Beta Metrics'}</strong>
          <p>{metricsError ?? readiness}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>Beta Decision</span>
          <strong>{metrics ? t(decisionLabel[metrics.betaDecision]) : '—'}</strong>
          <p>{metrics ? t(decisionDetail[metrics.betaDecision]) : t('等待指标加载。')}</p>
        </article>
      </div>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <div>
            <h2 className={styles.tableTitle}>{t('0.3.1 三端发布状态')}</h2>
            <p className={styles.sectionHint}>{t('人工审核记录与平台公开已发布版本分开显示；网络失败不会被判断为未发布。')}</p>
          </div>
          <button className={styles.searchBtn} disabled={publishedLoading} onClick={() => { void loadPublishedChannels(true) }}>
            {publishedLoading
              ? t('检查中…')
              : publishedCheckedAt
                ? `${t('检查平台版本')} · ${fmt(publishedCheckedAt)}`
                : t('检查平台版本')}
          </button>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>{t('渠道')}</th><th>{t('目标版本')}</th><th>{t('人工状态')}</th><th>{t('平台已发布')}</th><th>{t('核对结果')}</th><th>{t('备注')}</th><th>{t('操作')}</th></tr>
            </thead>
            <tbody>
              {releaseChannels.map(channel => {
                const published = publishedChannels.find(item => item.channel === channel.channel)
                const matchesTarget = published?.status === 'available' && published.version === channel.version
                return (
                  <tr key={channel.channel}>
                    <td>{t(CLIENT_CHANNEL_LABELS[channel.channel])}</td>
                    <td>
                      <input className={styles.searchInput} value={channel.version} onChange={event => updateReleaseChannel(channel.channel, { version: event.target.value })} />
                    </td>
                    <td>
                      <select className={styles.searchInput} value={channel.status} onChange={event => updateReleaseChannel(channel.channel, { status: event.target.value as ReleaseChannelStatus['status'] })}>
                        {['draft', 'packaged', 'submitted', 'in_review', 'approved', 'rejected', 'paused'].map(status => (
                          <option key={status} value={status}>{t(status)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {published?.status === 'available' ? (
                        <a href={published.sourceUrl} target="_blank" rel="noreferrer">{published.version}</a>
                      ) : published?.reason === 'platform_version_not_public' ? (
                        <a href={published.sourceUrl} target="_blank" rel="noreferrer">{t('平台未公开版本')}</a>
                      ) : published ? t('检查失败') : '—'}
                    </td>
                    <td>
                      {published?.status === 'available'
                        ? t(matchesTarget ? '目标版本已上线' : '目标版本尚未上线')
                        : published?.reason === 'platform_version_not_public'
                          ? t('仅保留人工记录')
                          : published
                            ? t('状态未知')
                            : '—'}
                    </td>
                    <td>
                      <input className={styles.searchInput} maxLength={500} value={channel.note} onChange={event => updateReleaseChannel(channel.channel, { note: event.target.value })} />
                      <small className={styles.sectionHint}>{channel.updatedAt ? `${fmt(channel.updatedAt)} · ${channel.updatedBy || '—'}` : '—'}</small>
                    </td>
                    <td>
                      <button className={styles.searchBtn} disabled={savingChannel === channel.channel} onClick={() => { void saveReleaseChannel(channel) }}>
                        {savingChannel === channel.channel ? t('保存中…') : t('保存')}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      {metrics?.betaWarnings?.length ? (
        <section className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h2 className={styles.tableTitle}>{t('当前预警')}</h2>
          </div>
          <div className={styles.checklist}>
            {metrics.betaWarnings.map(warning => (
              <div key={warning} className={styles.checkItem}>
                <span />
                <div>
                  <strong>{t('需要处理')}</strong>
                  <p>{t(warning)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('自然流量与公开测试指标')}</h2>
          <button className={styles.searchBtn} onClick={loadMetrics} disabled={metricsLoading}>
            {metricsLoading ? t('刷新中…') : metricsUpdatedAt ? `${t('刷新')} · ${fmt(metricsUpdatedAt)}` : t('刷新')}
          </button>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>{t('指标')}</th><th>{t('当前')}</th><th>{t('目标 / 解释')}</th></tr>
            </thead>
            <tbody>
              {goals.map(([name, value, detail]) => (
                <tr key={name}>
                  <td className={styles.emailCell}>{t(name)}</td>
                  <td>{t(value)}</td>
                  <td>{t(detail)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('Verify 客户端与版本分布')}</h2>
        </div>
        <div className={styles.statsGrid} style={{ padding: 16 }}>
          {clientChannelSummary.map(item => (
            <div key={item.clientChannel} className={styles.statCard}>
              <span className={styles.statValue}>{item.count}</span>
              <span className={styles.statLabel}>{t(CLIENT_CHANNEL_LABELS[item.clientChannel])}</span>
            </div>
          ))}
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>{t('客户端入口')}</th><th>{t('插件版本')}</th><th>{t('Build')}</th><th>{t('Manifest')}</th><th>{t('次数')}</th><th>{t('最近出现')}</th></tr>
            </thead>
            <tbody>
              {(metrics?.verifyVersionDistribution ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6}>{t('暂无版本数据')}</td>
                </tr>
              ) : metrics!.verifyVersionDistribution.map(item => (
                <tr key={`${item.clientChannel}-${item.extensionVersion}-${item.buildId}-${item.packagedAt}`}>
                  <td>{t(CLIENT_CHANNEL_LABELS[item.clientChannel] ?? '未知')}</td>
                  <td>{item.extensionVersion}</td>
                  <td className={styles.emailCell}>{item.buildId}</td>
                  <td>{item.manifestVersion || '—'}</td>
                  <td>{item.count}</td>
                  <td>{fmt(item.lastSeenAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('最近测试反馈')}</h2>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>{t('时间')}</th><th>{t('平台')}</th><th>{t('状态')}</th><th>{t('用户')}</th><th>{t('反馈')}</th></tr>
            </thead>
            <tbody>
              {(metrics?.recentFeedbackPackages ?? []).length === 0 ? (
                <tr>
                  <td colSpan={5}>{t('暂无测试反馈')}</td>
                </tr>
              ) : metrics!.recentFeedbackPackages.map(item => (
                <tr key={item.id}>
                  <td>{fmt(item.createdAt)}</td>
                  <td>{item.sourcePlatform}</td>
                  <td>{item.trustState || item.claimType || '—'}</td>
                  <td className={styles.emailCell}>{item.userId || '—'}</td>
                  <td>{item.feedbackText || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('最近阻断问题')}</h2>
        </div>
        {(metrics?.blockingIssueBreakdown ?? []).length > 0 ? (
          <div className={styles.moduleGrid} style={{ marginBottom: 12 }}>
            {metrics!.blockingIssueBreakdown.map(item => (
              <article key={item.area} className={styles.moduleCard}>
                <span className={styles.moduleLabel}>{item.area}</span>
                <strong>{item.count}</strong>
                <p>{t('未处理 blocking issue。')}</p>
              </article>
            ))}
          </div>
        ) : null}
        {(metrics?.blockingIssueTrend ?? []).length > 0 ? (
          <div className={styles.tableWrap} style={{ marginBottom: 12 }}>
            <table className={styles.table}>
              <thead>
                <tr><th>{t('日期')}</th><th>{t('区域')}</th><th>{t('次数')}</th></tr>
              </thead>
              <tbody>
                {metrics!.blockingIssueTrend.map(item => (
                  <tr key={`${item.day}-${item.area}`}>
                    <td>{item.day}</td>
                    <td>{item.area}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>{t('时间')}</th><th>{t('区域')}</th><th>{t('用户')}</th><th>{t('状态')}</th><th>{t('信息')}</th><th>{t('操作')}</th></tr>
            </thead>
            <tbody>
              {(metrics?.recentBlockingIssues ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6}>{t('暂无阻断问题')}</td>
                </tr>
              ) : metrics!.recentBlockingIssues.map(issue => (
                <tr key={issue.id}>
                  <td>{fmt(issue.createdAt)}</td>
                  <td>{issue.area}</td>
                  <td className={styles.emailCell}>{issue.userId || '—'}</td>
                  <td>{t(issue.issueStatus === 'resolved' ? '已处理' : '待处理')}</td>
                  <td>{issue.message || '—'}</td>
                  <td>
                    {issue.issueStatus === 'resolved' ? '—' : (
                      <button className={styles.searchBtn} onClick={() => resolveBlockingIssue(issue.id)}>
                        {t('标记已处理')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('阶段门槛')}</h2>
        </div>
        <div className={styles.checklist}>
          {gates.map(([title, body]) => (
            <div key={title} className={styles.checkItem}>
              <span />
              <div>
                <strong>{t(title)}</strong>
                <p>{t(body)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

// ── Nav config ────────────────────────────────────────────────────────────────

type NavView = 'overview' | 'beta' | 'users' | 'invites' | 'analytics' | 'sys'

const NAV_ITEMS: { view: NavView; icon: string; label: string; section?: string }[] = [
  { view: 'overview',    icon: 'OV', label: '运营概览', section: 'Spark Ops' },
  { view: 'beta',        icon: 'VT', label: '发布与验证' },
  { view: 'users',       icon: 'US', label: '账号权限' },
  { view: 'invites',     icon: 'IN', label: '注册与邀请' },
  { view: 'analytics',   icon: 'DA', label: '数据观察' },
  { view: 'sys',         icon: 'SY', label: '系统状态', section: 'Operations' },
]

// ── Main Admin Page ───────────────────────────────────────────────────────────

export default function Admin() {
  const navigate = useNavigate()
  const { token, email, logout } = useAuth()
  const api = useAdminApi()
  const adminI18n = useAdminI18n('spark-admin-root')

  const [activeView,   setActiveView]   = useState<NavView>('overview')
  const [stats,        setStats]        = useState<Stats | null>(null)
  const [users,        setUsers]        = useState<AdminUser[]>([])
  const [pagination,   setPagination]   = useState<Pagination | null>(null)
  const [page,         setPage]         = useState(1)
  const [search,       setSearch]       = useState('')
  const [searchInput,  setSearchInput]  = useState('')
  const [error,        setError]        = useState<string | null>(null)
  const [loadingUsers, setLoadingUsers] = useState(false)

  useEffect(() => {
    if (!token) navigate('/admin-login')
  }, [token, navigate])

  useEffect(() => {
    api.get('/stats')
      .then((d: { stats: Stats }) => setStats(d.stats))
      .catch((e: Error) => setError(e.message))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true); setError(null)
    try {
      const params = new URLSearchParams({
        page: String(page), limit: '20',
        ...(search ? { search } : {}),
      })
      const d = await api.get(`/users?${params}`) as { users: AdminUser[]; pagination: Pagination }
      setUsers(d.users)
      setPagination(d.pagination)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoadingUsers(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search])

  useEffect(() => { loadUsers() }, [loadUsers])

  const handleAction = async (userId: string, action: string, value?: string) => {
    try {
      if (action === 'plan')    await api.patch(`/users/${userId}/plan`,    { plan: value })
      if (action === 'enable')  await api.patch(`/users/${userId}/enable`)
      if (action === 'disable') await api.patch(`/users/${userId}/disable`)
      if (action === 'role')    await api.patch(`/users/${userId}/role`,    { role: value })
      if (action === 'source')  await api.patch(`/users/${userId}/source`,  { source: value })
      await loadUsers()
      const d = await api.get('/stats')
      setStats((d as { stats: Stats }).stats)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault(); setPage(1); setSearch(searchInput)
  }

  const fmt = (s: string) =>
    new Date(s).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })

  const renderContent = () => {
    switch (activeView) {
      case 'overview':
        return <SparkOpsOverviewView stats={stats} />

      case 'beta':
        return <PublicBetaMonitorView stats={stats} />

      case 'users':
        return (
          <>
            {stats && <StatsCards stats={stats} />}
            <section className={styles.tableSection}>
              <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>{adminI18n.t('用户列表')}</h2>
                <form onSubmit={handleSearch} className={styles.searchForm}>
                  <input
                    type="text" className={styles.searchInput}
                    placeholder={adminI18n.t('搜索邮箱…')}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                  />
                  <button type="submit" className={styles.searchBtn}>{adminI18n.t('搜索')}</button>
                  {search && (
                    <button type="button" className={styles.clearBtn}
                      onClick={() => { setSearchInput(''); setSearch(''); setPage(1) }}>
                      {adminI18n.t('清除')}
                    </button>
                  )}
                </form>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{adminI18n.t('邮箱')}</th><th>{adminI18n.t('方案')}</th><th>{adminI18n.t('角色')}</th>
                      <th>{adminI18n.t('来源')}</th><th>{adminI18n.t('状态')}</th><th>{adminI18n.t('注册时间')}</th><th>{adminI18n.t('邀请码')}</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingUsers ? (
                      <tr><td colSpan={8} className={styles.loadingRow}><span className={styles.pulse}>{adminI18n.t('加载中…')}</span></td></tr>
                    ) : users.length === 0 ? (
                      <tr><td colSpan={8} className={styles.emptyRow}>{adminI18n.t('未找到用户')}</td></tr>
                    ) : users.map(u => (
                      <tr key={u.id} className={`${styles.userRow} ${u.disabled_at ? styles.disabledRow : ''}`}>
                        <td className={styles.emailCell}>{u.email}</td>
                        <td><PlanBadge plan={u.plan} /></td>
                        <td><RoleBadge role={u.role} /></td>
                        <td><UserSourceBadge user={u} /></td>
                        <td>
                          <span className={`${styles.statusDot} ${u.disabled_at ? styles.statusOff : styles.statusOn}`}>
                            {adminI18n.t(u.disabled_at ? '已禁用' : '活跃')}
                          </span>
                        </td>
                        <td className={styles.dateCell}>{fmt(u.created_at)}</td>
                        <td className={styles.inviteCell}>{u.invite_code ?? '—'}</td>
                        <td><RowActions user={u} onAction={handleAction} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination && pagination.totalPages > 1 && (
                <div className={styles.pagination}>
                  <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← {adminI18n.t('上一页')}</button>
                  <span className={styles.pageInfo}>
                    {page} / {pagination.totalPages}
                    <span className={styles.pageTotalCount}> ({pagination.total} {adminI18n.t('用户')})</span>
                  </span>
                  <button className={styles.pageBtn} disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>{adminI18n.t('下一页')} →</button>
                </div>
              )}
            </section>
          </>
        )

      case 'analytics':
        return <AnalyticsView />

      case 'invites':
        return <InviteCodesView />

      case 'sys':
        return <SystemStatusView stats={stats} />

      default:
        return (
          <div style={{ padding: '32px 0', fontSize: 13, color: '#2a4a2a', fontFamily: 'var(--font-mono)' }}>
            {activeView} · 开发中
          </div>
        )
    }
  }

  return (
    <div id="spark-admin-root" className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.logo}>Spark</span>
          <span className={styles.headerSep}>/</span>
          <span className={styles.headerTitle}>OPS ADMIN</span>
        </div>
        <div className={styles.headerRight}>
          <span>{email ?? 'admin'}</span>
          <button className={styles.backBtn} type="button" onClick={adminI18n.switchLocale}>
            {adminI18n.switchLabel}
          </button>
          <button className={styles.backBtn} onClick={() => navigate('/kneadmin')}>
            KNE Admin
          </button>
          <button
            className={styles.backBtn}
            onClick={() => {
              logout()
              navigate('/admin-login')
            }}
          >
            {adminI18n.t('退出')}
          </button>
        </div>
      </header>

      <div className={styles.main}>
        <nav className={styles.nav}>
          {NAV_ITEMS.map(item => (
            <div key={item.view}>
              {item.section && <div className={styles.navSection}>{item.section}</div>}
              <button
                className={`${styles.navItem} ${activeView === item.view ? styles.navActive : ''}`}
                onClick={() => setActiveView(item.view)}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                {adminI18n.t(item.label)}
              </button>
            </div>
          ))}
        </nav>

        <main className={styles.content}>
          {error && (
            <div className={styles.errorBanner}>
              ⚠ {error}
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}
          {renderContent()}
        </main>
      </div>
    </div>
  )
}
